/**
 * Data of the search box (T11-07). The input is classified by `resolveQuery()`; a scanned / typed
 * code goes to `GET /v1/admin/variants/lookup?code=` at once (one hit → Enter opens the product),
 * text is debounced 200 ms and asks products, orders, customers and consignors at the same time
 * (5 each). Every source is asked only when the staff member may read it — a cashier never gets
 * customer rows (design/04 §14).
 */
import { useQuery } from "@tanstack/react-query";
import {
  createCatalogClient,
  createCmsClient,
  createConsignmentClient,
  createIdentityClient,
  createOrdersClient,
  type Permission,
} from "@tcg/api-client";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { allNavItems, NAV } from "@/app/nav";
import type { MoneyValue } from "@/components/Money";
import { ApiError, unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { usePermissions } from "@/lib/auth/usePermissions";
import { env } from "@/lib/env";

import { MIN_TEXT_LENGTH, type QueryKind, resolveQuery, TEXT_DEBOUNCE_MS } from "./resolver";

export const RESULT_LIMIT = 5;

export type SearchGroupKey =
  "lookup" | "actions" | "products" | "orders" | "customers" | "consignors" | "pages";

interface ItemBase {
  /** Unique within the palette. */
  id: string;
  /** Route (or URL for `external`). */
  to: string;
  external?: boolean;
  title: string;
  subtitle?: string;
}

export type SearchItem =
  | (ItemBase & { type: "action"; icon: "add" | "ship" | "pos" | "orders" | "content" })
  | (ItemBase & {
      type: "product";
      imageUrl: string | null;
      price: MoneyValue | null;
      status: string;
      condition: string | null;
      productType: string;
      /** How a lookup matched (`sku` / `barcode` / `cert_number`). */
      matchedBy?: string;
    })
  | (ItemBase & {
      type: "order";
      total: MoneyValue | null;
      status: string;
      fulfillmentStatus: string;
    })
  | (ItemBase & { type: "customer" })
  | (ItemBase & { type: "consignor"; status: string })
  | (ItemBase & { type: "page"; group: string });

export interface SearchGroup {
  key: SearchGroupKey;
  items: SearchItem[];
  loading: boolean;
  error: boolean;
}

export interface GlobalSearch {
  kind: QueryKind;
  term: string;
  groups: SearchGroup[];
  /** Still waiting for a source (debounce or request). */
  loading: boolean;
  /** Exactly one product found by a code lookup → Enter opens it. */
  exactMatch: SearchItem | null;
  /** A code lookup is still on its way (Enter waits for it). */
  lookupPending: boolean;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (delay <= 0) {
      return; // no wait: `value` is used directly below
    }
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return delay <= 0 ? value : debounced;
}

function money(value: { amount: string; currencyCode: string } | null | undefined) {
  return value ? { amount: value.amount, currencyCode: value.currencyCode } : null;
}

/** Content shortcuts (T11-42): the T09 screens staff open most. */
export const CONTENT_ACTIONS = {
  edit_home: "/online-store/themes/live/editor?page=index",
  edit_menus: "/content/menus",
  translate_missing: "/content/translations?missing=1",
} as const;

/** Actions reachable from the palette (design/04 §0 big buttons). */
function actionItems(
  can: (p: Permission) => boolean,
  t: (k: string, options?: Record<string, unknown>) => string,
  missingTranslations: number | null = null,
): SearchItem[] {
  const out: SearchItem[] = [];
  if (can("products.write")) {
    out.push({
      type: "action",
      id: "action:add_card",
      to: "/products/new",
      title: t("search:actions.add_card"),
      icon: "add",
    });
  }
  if (can("orders.read")) {
    out.push({
      type: "action",
      id: "action:to_ship",
      to: "/orders?view=to_ship",
      title: t("search:actions.to_ship"),
      icon: "ship",
    });
  }
  if (can("pos.sell")) {
    out.push({
      type: "action",
      id: "action:open_pos",
      to: env.VITE_POS_URL,
      external: true,
      title: t("search:actions.open_pos"),
      icon: "pos",
    });
  }
  if (can("cms.edit")) {
    out.push(
      {
        type: "action",
        id: "action:edit_home",
        to: CONTENT_ACTIONS.edit_home,
        title: t("search:actions.edit_home"),
        icon: "content",
      },
      {
        type: "action",
        id: "action:edit_menus",
        to: CONTENT_ACTIONS.edit_menus,
        title: t("search:actions.edit_menus"),
        icon: "content",
      },
      {
        type: "action",
        id: "action:translate_missing",
        to: CONTENT_ACTIONS.translate_missing,
        title:
          missingTranslations === null
            ? t("search:actions.translate_missing")
            : t("search:actions.translate_missing_count", { count: missingTranslations }),
        icon: "content",
      },
    );
  }
  return out;
}

function matches(haystack: string, needle: string): boolean {
  return haystack
    .toLocaleLowerCase()
    .normalize("NFKC")
    .includes(needle.toLocaleLowerCase().normalize("NFKC"));
}

export function useGlobalSearch(input: string, open = true): GlobalSearch {
  const { t } = useTranslation(["search", "common"]);
  const perms = usePermissions();
  const catalog = useServiceClient(createCatalogClient);
  const orders = useServiceClient(createOrdersClient);
  const identity = useServiceClient(createIdentityClient);
  const consignment = useServiceClient(createConsignmentClient);
  const cms = useServiceClient(createCmsClient);
  const canContent = perms.can("cms.edit");
  // "translate the empty texts (n)": texts still missing in every website language (cms)
  const translationSummary = useQuery({
    queryKey: ["search", "translations-missing"],
    enabled: open && canContent,
    meta: { silent: true },
    staleTime: 60_000,
    retry: false,
    queryFn: async ({ signal }) =>
      (await unwrap(cms.GET("/v1/admin/translations/summary", { signal }))).data.reduce(
        (sum, row) => sum + row.missing,
        0,
      ),
  });

  const resolved = resolveQuery(input);
  const debounceMs = resolved.kind === "text" ? TEXT_DEBOUNCE_MS : 0;
  const term = useDebounced(resolved.term, debounceMs);
  const settled = term === resolved.term;
  const kind = settled ? resolved.kind : resolveQuery(term).kind;
  const longEnough = term.length >= MIN_TEXT_LENGTH;

  const canProducts = perms.can("products.read");
  const canOrders = perms.can("orders.read");
  const canCustomers = perms.can("customers.read");
  const canConsignors = perms.can("consignment.read");

  const lookup = useQuery({
    queryKey: ["search", "lookup", term],
    enabled: open && kind === "code" && canProducts,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        return await unwrap(
          catalog.GET("/v1/admin/variants/lookup", { params: { query: { code: term } }, signal }),
        );
      } catch (error: unknown) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 422)) {
          return null;
        }
        throw error;
      }
    },
  });

  const products = useQuery({
    queryKey: ["search", "products", term],
    enabled: open && longEnough && canProducts && (kind === "text" || kind === "code"),
    meta: { silent: true },
    staleTime: 15_000,
    retry: false,
    queryFn: ({ signal }) =>
      unwrap(
        catalog.GET("/v1/admin/products", {
          params: { query: { q: term, limit: RESULT_LIMIT, sort: "relevance" } },
          signal,
        }),
      ),
  });

  const orderRows = useQuery({
    queryKey: ["search", "orders", term],
    enabled:
      open && longEnough && canOrders && (kind === "text" || kind === "order" || kind === "code"),
    meta: { silent: true },
    staleTime: 15_000,
    retry: false,
    queryFn: ({ signal }) =>
      unwrap(
        orders.GET("/v1/admin/orders", {
          params: { query: { q: term, limit: RESULT_LIMIT } },
          signal,
        }),
      ),
  });

  const customers = useQuery({
    queryKey: ["search", "customers", term],
    enabled: open && longEnough && canCustomers && kind === "text",
    meta: { silent: true },
    staleTime: 15_000,
    retry: false,
    queryFn: ({ signal }) =>
      unwrap(
        identity.GET("/v1/admin/customers", {
          params: { query: { q: term, limit: RESULT_LIMIT } },
          signal,
        }),
      ),
  });

  const consignors = useQuery({
    queryKey: ["search", "consignors", term],
    enabled: open && longEnough && canConsignors && (kind === "text" || kind === "consignor"),
    meta: { silent: true },
    staleTime: 15_000,
    retry: false,
    queryFn: ({ signal }) =>
      unwrap(
        consignment.GET("/v1/admin/consignors", {
          params: { query: { q: term, limit: RESULT_LIMIT } },
          signal,
        }),
      ),
  });

  return useMemo<GlobalSearch>(() => {
    const groups: SearchGroup[] = [];
    const tt = (key: string, options?: Record<string, unknown>) => t(key, options);

    // ---- exact code match -------------------------------------------------------------------
    let exactMatch: SearchItem | null = null;
    if (kind === "code" && lookup.data) {
      const hit = lookup.data;
      exactMatch = {
        type: "product",
        id: `product:${hit.product.id}`,
        to: `/products/${hit.product.id}`,
        title: hit.product.title,
        subtitle: [hit.variant.sku, hit.variant.certNumber].filter(Boolean).join(" · "),
        imageUrl: null,
        price: money(hit.variant.pricing.price),
        status: hit.product.status,
        condition: hit.variant.rawRank ?? hit.variant.gradeLabel ?? null,
        productType: hit.product.productType,
        matchedBy: hit.matchedBy,
      };
      groups.push({ key: "lookup", items: [exactMatch], loading: false, error: false });
    }

    // ---- actions + pages (typed words match their names) ------------------------------------
    const actions = actionItems(perms.can, tt, translationSummary.data ?? null).filter(
      (item) => resolved.kind === "empty" || matches(item.title, resolved.term),
    );
    if (actions.length) {
      groups.push({ key: "actions", items: actions, loading: false, error: false });
    }

    if (products.data && kind !== "order" && kind !== "consignor") {
      const items: SearchItem[] = products.data.data
        .filter((row) => !exactMatch || `product:${row.id}` !== exactMatch.id)
        .map((row) => ({
          type: "product",
          id: `product:${row.id}`,
          to: `/products/${row.id}`,
          title: row.title,
          subtitle: [
            row.sku,
            row.certNumber,
            row.setCode && row.cardNumber ? `${row.setCode} ${row.cardNumber}` : row.setName,
          ]
            .filter(Boolean)
            .join(" · "),
          imageUrl: row.image?.url ?? null,
          price: money(row.price),
          status: row.status,
          condition: row.conditionLabel,
          productType: row.productType,
        }));
      if (items.length) {
        groups.push({ key: "products", items, loading: false, error: false });
      }
    } else if ((products.isFetching || products.isError) && canProducts) {
      groups.push({
        key: "products",
        items: [],
        loading: products.isFetching,
        error: products.isError,
      });
    }

    if (orderRows.data) {
      const items: SearchItem[] = orderRows.data.data.map((row) => ({
        type: "order",
        id: `order:${row.id}`,
        to: `/orders/${row.id}`,
        title: row.number,
        subtitle: row.customerName ?? undefined,
        total: money(row.total),
        status: row.status,
        fulfillmentStatus: row.fulfillmentStatus,
      }));
      if (items.length) {
        groups.push({ key: "orders", items, loading: false, error: false });
      }
    } else if ((orderRows.isFetching || orderRows.isError) && canOrders) {
      groups.push({
        key: "orders",
        items: [],
        loading: orderRows.isFetching,
        error: orderRows.isError,
      });
    }

    if (customers.data) {
      const items: SearchItem[] = customers.data.data.map((row) => ({
        type: "customer",
        id: `customer:${row.id}`,
        to: `/customers/${row.id}`,
        title: row.name ?? row.email,
        subtitle: row.name ? row.email : undefined,
      }));
      if (items.length) {
        groups.push({ key: "customers", items, loading: false, error: false });
      }
    } else if ((customers.isFetching || customers.isError) && canCustomers) {
      groups.push({
        key: "customers",
        items: [],
        loading: customers.isFetching,
        error: customers.isError,
      });
    }

    if (consignors.data) {
      const items: SearchItem[] = consignors.data.data.map((row) => ({
        type: "consignor",
        id: `consignor:${row.id}`,
        to: `/consignment/consignors/${row.id}`,
        title: `${row.reference} · ${row.displayName}`,
        subtitle: row.nameKana ?? undefined,
        status: row.status,
      }));
      if (items.length) {
        groups.push({ key: "consignors", items, loading: false, error: false });
      }
    } else if ((consignors.isFetching || consignors.isError) && canConsignors) {
      groups.push({
        key: "consignors",
        items: [],
        loading: consignors.isFetching,
        error: consignors.isError,
      });
    }

    // ---- menu pages (settings etc.) whose name contains the words ---------------------------
    if (resolved.kind === "text" && resolved.term.length >= 1) {
      const groupOf = new Map<string, string>();
      for (const group of NAV) {
        for (const item of group.items) {
          groupOf.set(item.to, group.key);
        }
      }
      const pages: SearchItem[] = allNavItems()
        .filter((item) => !item.comingSoon && perms.allows(item.access))
        .map((item) => ({
          type: "page" as const,
          id: `page:${item.to}`,
          to: item.to,
          title: t(`common:nav.items.${item.key}`),
          group: t(`common:nav.groups.${groupOf.get(item.to) ?? "home"}`),
        }))
        .filter((item) => matches(item.title, resolved.term) || matches(item.group, resolved.term))
        .slice(0, RESULT_LIMIT)
        .map((item) => ({ ...item, subtitle: item.group }));
      if (pages.length) {
        groups.push({ key: "pages", items: pages, loading: false, error: false });
      }
    }

    const lookupPending = kind === "code" && canProducts && (lookup.isFetching || !settled);
    const loading =
      !settled ||
      lookup.isFetching ||
      products.isFetching ||
      orderRows.isFetching ||
      customers.isFetching ||
      consignors.isFetching;
    return { kind: resolved.kind, term: resolved.term, groups, loading, exactMatch, lookupPending };
  }, [
    t,
    perms,
    translationSummary.data,
    kind,
    settled,
    resolved.kind,
    resolved.term,
    lookup.data,
    lookup.isFetching,
    products.data,
    products.isFetching,
    products.isError,
    orderRows.data,
    orderRows.isFetching,
    orderRows.isError,
    customers.data,
    customers.isFetching,
    customers.isError,
    consignors.data,
    consignors.isFetching,
    consignors.isError,
    canProducts,
    canOrders,
    canCustomers,
    canConsignors,
  ]);
}
